import {
  ADDRESS_MASTER_METADATA,
  ADDRESS_MASTER_RECORDS,
  type AddressMasterKind,
  type AddressMasterRecord,
} from '@shared/data/address-master.generated';

export { ADDRESS_MASTER_METADATA };

interface AddressSimpleOption {
  readonly value: string;
  readonly label: string;
}

export interface MunicipalityOption extends AddressSimpleOption {
  readonly code: string;
  readonly prefecture: string;
  readonly county: string | null;
  readonly city: string;
  readonly kind: Exclude<AddressMasterKind, 'ward'>;
  readonly requiresWard: boolean;
}

export interface WardOption extends AddressSimpleOption {
  readonly code: string;
  readonly parentCode: string;
}

const municipalities = ADDRESS_MASTER_RECORDS.filter(
  (
    record,
  ): record is AddressMasterRecord & {
    kind: Exclude<AddressMasterKind, 'ward'>;
  } => record.kind !== 'ward' && record.uiSelectable,
);

const wards = ADDRESS_MASTER_RECORDS.filter(
  (
    record,
  ): record is AddressMasterRecord & {
    kind: 'ward';
    ward: string;
    parentCode: string;
  } =>
    record.kind === 'ward' &&
    record.ward !== null &&
    record.parentCode !== null &&
    record.uiSelectable,
);

const municipalityByCode = new Map(municipalities.map((record) => [record.code, record]));

const municipalityOptionsByPrefecture = new Map<string, MunicipalityOption[]>();
for (const record of municipalities) {
  const current = municipalityOptionsByPrefecture.get(record.prefecture) ?? [];
  current.push({
    value: record.code,
    label: record.county ? `${record.county}${record.city}` : record.city,
    code: record.code,
    prefecture: record.prefecture,
    county: record.county,
    city: record.city,
    kind: record.kind,
    requiresWard: record.kind === 'designated_city',
  });
  municipalityOptionsByPrefecture.set(record.prefecture, current);
}

const wardOptionsByParentCode = new Map<string, WardOption[]>();
for (const record of wards) {
  const current = wardOptionsByParentCode.get(record.parentCode) ?? [];
  current.push({
    value: record.ward,
    label: record.ward,
    code: record.code,
    parentCode: record.parentCode,
  });
  wardOptionsByParentCode.set(record.parentCode, current);
}

export const ADDRESS_PREFECTURE_OPTIONS: readonly AddressSimpleOption[] = Array.from(
  municipalityOptionsByPrefecture.keys(),
  (prefecture) => ({
    value: prefecture,
    label: prefecture,
  }),
);

export function municipalitiesForPrefecture(
  prefecture: string | null | undefined,
): readonly MunicipalityOption[] {
  if (!prefecture) return [];
  return municipalityOptionsByPrefecture.get(prefecture) ?? [];
}

export function wardsForMunicipality(
  municipalityCode: string | null | undefined,
): readonly WardOption[] {
  if (!municipalityCode) return [];
  return wardOptionsByParentCode.get(municipalityCode) ?? [];
}

export function municipalityForCode(
  municipalityCode: string | null | undefined,
): MunicipalityOption | null {
  if (!municipalityCode) return null;
  const record = municipalityByCode.get(municipalityCode);
  if (!record) return null;
  return {
    value: record.code,
    label: record.county ? `${record.county}${record.city}` : record.city,
    code: record.code,
    prefecture: record.prefecture,
    county: record.county,
    city: record.city,
    kind: record.kind,
    requiresWard: record.kind === 'designated_city',
  };
}

export function findMunicipalityCode(
  prefecture: string | null | undefined,
  county: string | null | undefined,
  city: string | null | undefined,
): string | null {
  if (!prefecture || !city) return null;
  const matches = municipalitiesForPrefecture(prefecture).filter(
    (option) => option.city === city && option.county === (county ?? null),
  );
  return matches.length === 1 ? (matches[0]?.code ?? null) : null;
}

export function municipalityLabel(
  prefecture: string | null | undefined,
  county: string | null | undefined,
  city: string | null | undefined,
): string {
  if (!city) return '';
  const code = findMunicipalityCode(prefecture, county, city);
  return municipalityForCode(code)?.label ?? `${county ?? ''}${city}`;
}
